// src/storage/useAppState.js
// Migrated verbatim from prototype/between-the-lines-v2.html lines 5871-6141.
// localStorage key shape preserved byte-for-byte (every btl_v2_* key matches prototype).
// Import list per amendment v1.1 §3.6 (16 named imports across 11 config modules).

import { useState, useEffect } from 'react';
import { AUTH_MODE, db } from '../auth/firebase.js';

// Per spec v1.0:
import { DEFAULT_SITE_TEXT } from '../config/siteText.js';
import { sanitizeCachedSiteText } from '../config/homeImages.js';
import { DEFAULT_BOOKING } from '../config/booking.js';
import { DEFAULT_CHECKOUT } from '../config/checkout.js';
import { DEFAULT_TUTOR_REVIEW_SETTINGS } from '../config/tutorReview.js';

// Per amendment v1.1 (4 new imports):
import { DEFAULT_ACCOUNT, DEFAULT_EXAM_DATES, DEFAULT_EMAIL_NOTIFICATIONS } from '../config/account.js';
import { DEFAULT_BASKET, DEFAULT_TERMINOLOGY, DEFAULT_TERMINOLOGY_CATEGORIES } from '../config/basket.js';

export const useAppState = () => {
    const [user, setUser] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_user'); return s ? JSON.parse(s) : null; } catch { return null; }
    });
    const [cart, setCart] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_cart'); return s ? JSON.parse(s) : []; } catch { return []; }
    });
    // WP-3: ownership is now derived from Firestore entitlements (useEntitlements),
    // not localStorage. Kept as an in-memory field for legacy readers, NOT persisted.
    const [purchased, setPurchased] = useState([]);
    // Feature 6: User's credit balance (Hybrid schema — tutorReview implemented Phase 0,
    // universal reserved for Phase 1.5+ AI features like AI review / AI study partner).
    const [userCredits, setUserCredits] = useState(() => {
        try {
            const s = localStorage.getItem('btl_v2_userCredits');
            if (!s) return { tutorReview: 0, universal: 0 };
            const parsed = JSON.parse(s);
            return { tutorReview: 0, universal: 0, ...parsed };
        } catch { return { tutorReview: 0, universal: 0 }; }
    });
    // Feature 6: Audit log of credit pack purchases (for admin reports + history)
    const [creditPurchases, setCreditPurchases] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_creditPurchases'); return s ? JSON.parse(s) : []; } catch { return []; }
    });
    const [bookmarks, setBookmarks] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_bookmarks'); return s ? JSON.parse(s) : []; } catch { return []; }
    });
    const [notes, setNotes] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_notes'); return s ? JSON.parse(s) : {}; } catch { return {}; }
    });
    // NOTE: completedTexts/quizScores were localStorage-only and ORPHANED (no writers).
    // Progress is now persisted per-user in Firestore -- see useProgress.js / the
    // dashboard stats / the reader progress bars. Removed here (Scope A).
    const [examDate, setExamDate] = useState(() => {
        try { return localStorage.getItem('btl_v2_examDate') || ''; } catch { return ''; }
    });

    // ── Admin-editable content ──
    const [adminSession, setAdminSession] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_adminSession'); return s ? JSON.parse(s) : null; } catch { return null; }
    });
    const [siteText, setSiteText] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_siteText'); return s ? { ...DEFAULT_SITE_TEXT, ...sanitizeCachedSiteText(JSON.parse(s)) } : DEFAULT_SITE_TEXT; } catch { return DEFAULT_SITE_TEXT; }
    });
    // Slice 1 de-seed: NO bundled demo packs. The live catalog comes from Firestore /packs
    // via usePacksBridge (firebase/emulator); mock mode shows a clean empty storefront.
    const [packs, setPacks] = useState([]);
    // True while the live /packs bridge is in flight (firebase/emulator only) so the
    // storefront can show a loading state instead of an empty flash. False in mock / no-db
    // (no fetch -> empty storefront immediately). usePacksBridge clears it after the read.
    const [packsLoading, setPacksLoading] = useState(() => AUTH_MODE !== 'mock' && !!db);
    // Slice 1 de-seed: legacy in-memory study-app stores start EMPTY (no bundled demo set
    // texts / flashcards / quizzes / audio). Real content is read from Firestore by the
    // readers (usePackItems); these fields persist only what the user/app writes.
    const [texts, setTexts] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_texts'); return s ? JSON.parse(s) : []; } catch { return []; }
    });
    const [flashcardsByText, setFlashcardsByText] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_flashcardsByText'); return s ? JSON.parse(s) : {}; } catch { return {}; }
    });
    const [quizzesByText, setQuizzesByText] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_quizzesByText'); return s ? JSON.parse(s) : {}; } catch { return {}; }
    });
    const [mixedQuizzesAll, setMixedQuizzesAll] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_mixedQuizzes'); return s ? JSON.parse(s) : []; } catch { return []; }
    });
    const [audiosByText, setAudiosByText] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_audiosByText'); return s ? JSON.parse(s) : {}; } catch { return {}; }
    });
    // Per-pack content cache (badges/progress fall back to live pack.freeItems when empty).
    const [packContent, setPackContent] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_packContent'); return s ? JSON.parse(s) : {}; } catch { return {}; }
    });
    const [bookingContent, setBookingContent] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_bookingContent'); return s ? { ...DEFAULT_BOOKING, ...JSON.parse(s) } : DEFAULT_BOOKING; } catch { return DEFAULT_BOOKING; }
    });
    const [accountContent, setAccountContent] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_accountContent'); return s ? { ...DEFAULT_ACCOUNT, ...JSON.parse(s) } : DEFAULT_ACCOUNT; } catch { return DEFAULT_ACCOUNT; }
    });
    const [basketContent, setBasketContent] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_basketContent'); return s ? { ...DEFAULT_BASKET, ...JSON.parse(s) } : DEFAULT_BASKET; } catch { return DEFAULT_BASKET; }
    });
    const [checkoutContent, setCheckoutContent] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_checkoutContent'); return s ? { ...DEFAULT_CHECKOUT, ...JSON.parse(s) } : DEFAULT_CHECKOUT; } catch { return DEFAULT_CHECKOUT; }
    });
    // WP-3: deprecated. Firestore entitlements are the source of truth; kept
    // in-memory only for any legacy reader. NOT persisted.
    const [subscriptions, setSubscriptions] = useState({});
    const [terminology, setTerminology] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_terminology'); return s ? JSON.parse(s) : DEFAULT_TERMINOLOGY; } catch { return DEFAULT_TERMINOLOGY; }
    });
    const [terminologyCategories, setTerminologyCategories] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_terminologyCategories'); return s ? JSON.parse(s) : DEFAULT_TERMINOLOGY_CATEGORIES; } catch { return DEFAULT_TERMINOLOGY_CATEGORIES; }
    });
    const [examDates, setExamDates] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_examDates'); return s ? JSON.parse(s) : DEFAULT_EXAM_DATES; } catch { return DEFAULT_EXAM_DATES; }
    });
    const [emailNotifications, setEmailNotifications] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_emailNotifications'); return s ? { ...DEFAULT_EMAIL_NOTIFICATIONS, ...JSON.parse(s) } : DEFAULT_EMAIL_NOTIFICATIONS; } catch { return DEFAULT_EMAIL_NOTIFICATIONS; }
    });
    // Model-B blank shape (Slice 1): merge any stored fields over the blank scaffold.
    const [tutorReviewSettings, setTutorReviewSettings] = useState(() => {
        try {
            const s = localStorage.getItem('btl_v2_tutorReviewSettings');
            if (!s) return { ...DEFAULT_TUTOR_REVIEW_SETTINGS };
            return { ...DEFAULT_TUTOR_REVIEW_SETTINGS, ...JSON.parse(s) };
        } catch { return { ...DEFAULT_TUTOR_REVIEW_SETTINGS }; }
    });
    const [submissions, setSubmissions] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_submissions'); return s ? JSON.parse(s) : {}; } catch { return {}; }
    });
    const [tutorReviewDisabled, setTutorReviewDisabled] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_tutorReviewDisabled'); return s ? JSON.parse(s) : {}; } catch { return {}; }
    });
    const [bookings, setBookings] = useState(() => {
        try { const s = localStorage.getItem('btl_v2_bookings'); return s ? JSON.parse(s) : {}; } catch { return {}; }
    });
    // WP-3: manual purchases now live in Firestore /pendingPurchases
    // (usePendingPurchases). Kept in-memory only; NOT persisted.
    const [pendingPurchases, setPendingPurchases] = useState({});

    useEffect(() => { try { localStorage.setItem('btl_v2_user', JSON.stringify(user)); } catch {} }, [user]);
    useEffect(() => { try { localStorage.setItem('btl_v2_cart', JSON.stringify(cart)); } catch {} }, [cart]);
    // WP-3: purchased is no longer persisted (Firestore entitlements are truth).
    useEffect(() => { try { localStorage.setItem('btl_v2_bookmarks', JSON.stringify(bookmarks)); } catch {} }, [bookmarks]);
    useEffect(() => { try { localStorage.setItem('btl_v2_notes', JSON.stringify(notes)); } catch {} }, [notes]);
    useEffect(() => { try { localStorage.setItem('btl_v2_examDate', examDate); } catch {} }, [examDate]);

    useEffect(() => { try { localStorage.setItem('btl_v2_adminSession', JSON.stringify(adminSession)); } catch {} }, [adminSession]);
    useEffect(() => { try { localStorage.setItem('btl_v2_siteText', JSON.stringify(siteText)); } catch {} }, [siteText]);
    useEffect(() => { try { localStorage.setItem('btl_v2_packs', JSON.stringify(packs)); } catch {} }, [packs]);
    useEffect(() => { try { localStorage.setItem('btl_v2_texts', JSON.stringify(texts)); } catch {} }, [texts]);
    useEffect(() => { try { localStorage.setItem('btl_v2_flashcardsByText', JSON.stringify(flashcardsByText)); } catch {} }, [flashcardsByText]);
    useEffect(() => { try { localStorage.setItem('btl_v2_quizzesByText', JSON.stringify(quizzesByText)); } catch {} }, [quizzesByText]);
    useEffect(() => { try { localStorage.setItem('btl_v2_mixedQuizzes', JSON.stringify(mixedQuizzesAll)); } catch {} }, [mixedQuizzesAll]);
    useEffect(() => { try { localStorage.setItem('btl_v2_audiosByText', JSON.stringify(audiosByText)); } catch {} }, [audiosByText]);
    useEffect(() => { try { localStorage.setItem('btl_v2_packContent', JSON.stringify(packContent)); } catch {} }, [packContent]);
    useEffect(() => { try { localStorage.setItem('btl_v2_bookingContent', JSON.stringify(bookingContent)); } catch {} }, [bookingContent]);
    useEffect(() => { try { localStorage.setItem('btl_v2_accountContent', JSON.stringify(accountContent)); } catch {} }, [accountContent]);
    useEffect(() => { try { localStorage.setItem('btl_v2_basketContent', JSON.stringify(basketContent)); } catch {} }, [basketContent]);
    useEffect(() => { try { localStorage.setItem('btl_v2_checkoutContent', JSON.stringify(checkoutContent)); } catch {} }, [checkoutContent]);
    // WP-3: subscriptions no longer persisted (Firestore entitlements are truth).
    useEffect(() => { try { localStorage.setItem('btl_v2_terminology', JSON.stringify(terminology)); } catch {} }, [terminology]);
    useEffect(() => { try { localStorage.setItem('btl_v2_terminologyCategories', JSON.stringify(terminologyCategories)); } catch {} }, [terminologyCategories]);

    useEffect(() => { try { localStorage.setItem('btl_v2_examDates', JSON.stringify(examDates)); } catch {} }, [examDates]);
    useEffect(() => { try { localStorage.setItem('btl_v2_emailNotifications', JSON.stringify(emailNotifications)); } catch {} }, [emailNotifications]);
    useEffect(() => { try { localStorage.setItem('btl_v2_tutorReviewSettings', JSON.stringify(tutorReviewSettings)); } catch {} }, [tutorReviewSettings]);
    useEffect(() => { try { localStorage.setItem('btl_v2_userCredits', JSON.stringify(userCredits)); } catch {} }, [userCredits]);
    useEffect(() => { try { localStorage.setItem('btl_v2_creditPurchases', JSON.stringify(creditPurchases)); } catch {} }, [creditPurchases]);
    useEffect(() => { try { localStorage.setItem('btl_v2_submissions', JSON.stringify(submissions)); } catch {} }, [submissions]);
    useEffect(() => { try { localStorage.setItem('btl_v2_tutorReviewDisabled', JSON.stringify(tutorReviewDisabled)); } catch {} }, [tutorReviewDisabled]);
    useEffect(() => { try { localStorage.setItem('btl_v2_bookings', JSON.stringify(bookings)); } catch {} }, [bookings]);
    // WP-3: pendingPurchases no longer persisted (Firestore /pendingPurchases is truth).

    // WP-3: one-time removal of the now-stale money/entitlement localStorage keys.
    useEffect(() => {
        try {
            ['btl_v2_purchased', 'btl_v2_subscriptions', 'btl_v2_pendingPurchases']
                .forEach(k => localStorage.removeItem(k));
        } catch {}
    }, []);

    return {
        user, setUser, cart, setCart, purchased, setPurchased,
        bookmarks, setBookmarks, notes, setNotes,
        examDate, setExamDate,
        // admin-editable content
        adminSession, setAdminSession,
        siteText, setSiteText,
        packs, setPacks,
        packsLoading, setPacksLoading,
        texts, setTexts,
        flashcardsByText, setFlashcardsByText,
        quizzesByText, setQuizzesByText,
        mixedQuizzesAll, setMixedQuizzesAll,
        audiosByText, setAudiosByText,
        packContent, setPackContent,
        bookingContent, setBookingContent,
        accountContent, setAccountContent,
        basketContent, setBasketContent,
        checkoutContent, setCheckoutContent,
        subscriptions, setSubscriptions,
        terminology, setTerminology,
        terminologyCategories, setTerminologyCategories,
        examDates, setExamDates,
        emailNotifications, setEmailNotifications,
        tutorReviewSettings, setTutorReviewSettings,
        submissions, setSubmissions,
        tutorReviewDisabled, setTutorReviewDisabled,
        bookings, setBookings,
        pendingPurchases, setPendingPurchases,
        // Feature 6: tutor review extra quota credits
        userCredits, setUserCredits,
        creditPurchases, setCreditPurchases
    };
};

export default useAppState;
